import { type FormField, formFieldPresentation } from '@/components/shared/form/form-fields';
import { CheckboxInput } from '@/components/ui/form/checkbox-input';
import { SelectInput } from '@/components/ui/form/select-input';
import { TextInput } from '@/components/ui/form/text-input';
import { TextareaInput } from '@/components/ui/form/textarea-input';
import { MultipleChoiceField } from './multiple-choice-field';
import { NameSeparateField } from './name-separate-field';
import { SingleChoiceField } from './single-choice-field';

function MultilineFormField({
  id,
  field,
  value,
  disabled,
  onChange,
}: {
  id: string;
  field: FormField;
  value: string;
  disabled: boolean;
  onChange: (val: string) => void;
}) {
  const presentation = formFieldPresentation(field);

  return (
    <TextareaInput
      id={id}
      label={field.label}
      placeholder={field.placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      isRequired={field.required}
      showAsterisk={presentation.showAsterisk}
      helperText={presentation.helperText}
      helperTextPlacement="afterControl"
      disabled={disabled}
      required={field.required}
      rows={4}
      fieldSizing="fixed"
      className="[&_[data-slot=textarea]]:resize-y"
    />
  );
}

export function FormFieldInput({
  id,
  field,
  value,
  disabled,
  onChange,
}: {
  id: string;
  field: FormField;
  value: string | string[];
  disabled: boolean;
  onChange: (val: string | string[]) => void;
}) {
  const presentation = formFieldPresentation(field);

  if (field.type === 'name' && field.separateFirstLast) {
    return (
      <NameSeparateField
        id={id}
        field={field}
        value={value as string}
        disabled={disabled}
        onChange={onChange as (v: string) => void}
      />
    );
  }

  if (field.type === 'dropdown') {
    const selectOptions = (field.options ?? []).map((opt) => ({
      value: opt.value,
      title: opt.label,
    }));
    return (
      <SelectInput
        id={id}
        label={field.label}
        options={selectOptions}
        value={value as string}
        onValueChange={(val) => onChange(val)}
        placeholder={field.placeholder ?? 'Select an option'}
        isRequired={field.required}
        showAsterisk={presentation.showAsterisk}
        helperText={presentation.helperText}
        helperTextPlacement="afterControl"
        disabled={disabled}
        searchable={false}
      />
    );
  }

  if (field.type === 'single_choice') {
    return (
      <SingleChoiceField
        id={id}
        field={field}
        value={value as string}
        disabled={disabled}
        onChange={onChange as (v: string) => void}
      />
    );
  }

  if (field.type === 'multiple_choice') {
    return (
      <MultipleChoiceField
        id={id}
        field={field}
        value={value as string[]}
        disabled={disabled}
        onChange={onChange as (v: string[]) => void}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <CheckboxInput
        id={id}
        label={field.label}
        checked={(value as string) === 'true'}
        onCheckedChange={(checked) => onChange(checked ? 'true' : '')}
        isRequired={field.required}
        showAsterisk={presentation.showAsterisk}
        helperText={presentation.helperText}
        helperTextPlacement="afterControl"
        disabled={disabled}
      />
    );
  }

  if (field.type === 'textarea' || field.type === 'multiline') {
    return (
      <MultilineFormField
        id={id}
        field={field}
        value={value as string}
        disabled={disabled}
        onChange={onChange as (val: string) => void}
      />
    );
  }

  const inputType =
    field.type === 'email'
      ? 'email'
      : field.type === 'tel' || field.type === 'phone'
        ? 'tel'
        : 'text';

  return (
    <TextInput
      id={id}
      type={inputType}
      label={field.label}
      placeholder={field.placeholder}
      value={value as string}
      onChange={(e) => onChange(e.target.value)}
      isRequired={field.required}
      showAsterisk={presentation.showAsterisk}
      helperText={presentation.helperText}
      helperTextPlacement="afterControl"
      disabled={disabled}
      required={field.required}
    />
  );
}
